import type { Page } from '@/types';

// The API's largest page (its paging bounds): it refuses a larger `limit`.
const MAX_PAGE_SIZE = 200;

export type PageRequest = { limit: number; offset: number };

// A list's loaded rows read again, when a write changed what they show (VER-571): as many as are loaded, a first page
// at least, in pages the API takes, so "Carregar mais" isn't undone. `pageOf` finds the page in an answer (an
// invoice's charges sit under its header); `append` puts the next answer's rows after the ones read.
export async function rereadLoadedPages<R>(
  read: (page: PageRequest) => Promise<R>,
  pageOf: (answer: R) => Page<unknown>,
  append: (answer: R, next: R) => R,
  loaded: number,
  pageSize: number,
): Promise<R> {
  const shown = Math.max(pageSize, loaded);
  let answer: R = await read({ limit: Math.min(MAX_PAGE_SIZE, shown), offset: 0 });
  let next = pageOf(answer).nextOffset;
  while (next !== null && next < shown) {
    answer = append(answer, await read({ limit: Math.min(MAX_PAGE_SIZE, shown - next), offset: next }));
    next = pageOf(answer).nextOffset;
  }
  return answer;
}

// The next page's rows after the ones read, under the next page's paging.
export function appendPage<T>(answer: Page<T>, next: Page<T>): Page<T> {
  return { ...next, items: [...answer.items, ...next.items] };
}

export function pageItself<T>(page: Page<T>): Page<T> {
  return page;
}
